import { useEffect, useState, type ReactNode } from "react";

import type { TenantTheme } from "@l30/shared";

/**
 * ThemeProvider — applies per-tenant CSS-var overrides at the document
 * root. Per-tenant white-label without a redeploy.
 *
 * Canon: docs/design-canon/08-design/theming.md.
 *
 * Behaviour:
 *  - Reads `theme` (a partial TenantTheme object — every key optional).
 *  - For each provided semantic-token key, writes the value as
 *    `--color-<kebab>` on `document.documentElement`. The CSS cascade
 *    propagates to every utility class without any React fan-out.
 *  - On unmount or theme change, restores the previous value (or
 *    removes the property entirely if there was none).
 *
 * Mounting: once at the root of the authenticated layout. The default
 * theme (from globals.css `:root`) renders before any override is
 * applied — that's the brand-spec default and a safe fallback during
 * the first paint while tenant config is still loading.
 *
 * Per canon R6: this is the ONLY component that writes `--color-*`
 * vars. Components and effects must not call
 * `style.setProperty("--color-...", ...)` directly. A future
 * `eslint-plugin-l30/no-direct-css-var-write` rule will enforce this.
 */
/**
 * Colour scheme for the staff app (ADR-0078 X9 — dark staff theme).
 *  - `"light"` — the brand default (globals.css `:root`).
 *  - `"dark"`  — the "Bloomberg terminal" ramp (`:root[data-theme="dark"]`).
 *  - `"system"`— follows the OS via `@media (prefers-color-scheme: dark)`.
 */
export type ColorScheme = "light" | "dark" | "system";

const COLOR_SCHEME_KEY = "l30:color-scheme";
const COLOR_SCHEME_EVENT = "l30:color-scheme-change";

function readStoredColorScheme(): ColorScheme {
  try {
    if (typeof localStorage === "undefined" || typeof localStorage.getItem !== "function") {
      return "light";
    }
    const v = localStorage.getItem(COLOR_SCHEME_KEY);
    return v === "dark" || v === "system" || v === "light" ? v : "light";
  } catch {
    return "light";
  }
}

/** Stored preference, including `"system"`. Updates when `setColorScheme` runs. */
export function useColorScheme(): ColorScheme {
  const [scheme, setScheme] = useState<ColorScheme>(readStoredColorScheme);
  useEffect(() => {
    const onChange = () => setScheme(readStoredColorScheme());
    window.addEventListener(COLOR_SCHEME_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(COLOR_SCHEME_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);
  return scheme;
}

/**
 * Persist and broadcast a colour-scheme choice. Exposed so a settings
 * toggle (or the console, during a demo) can flip the staff app between
 * light / dark / system without a redeploy — every mounted ThemeProvider
 * re-stamps `data-theme` on the root in response.
 */
export function setColorScheme(scheme: ColorScheme): void {
  try {
    if (typeof localStorage !== "undefined" && typeof localStorage.setItem === "function") {
      localStorage.setItem(COLOR_SCHEME_KEY, scheme);
    }
  } catch {
    // Ignore quota / privacy-mode failures — scheme still applies in-memory via event.
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(COLOR_SCHEME_EVENT, { detail: scheme }));
  }
}

export interface ThemeProviderProps {
  /**
   * Partial tenant theme override. When undefined, defaults from
   * globals.css apply unchanged (no DOM mutation occurs).
   */
  theme?: TenantTheme;
  /**
   * Explicit colour scheme. When provided it wins over the persisted
   * setting (useful for previews/tests). When omitted, the persisted
   * `l30:color-scheme` value is used (default `"light"`).
   */
  colorScheme?: ColorScheme;
  children: ReactNode;
}

export function ThemeProvider({ theme, colorScheme, children }: ThemeProviderProps) {
  useEffect(() => {
    if (!theme) return;

    const root = document.documentElement;
    const previous: Array<[cssVar: string, prevValue: string]> = [];

    for (const [key, value] of Object.entries(theme)) {
      if (typeof value !== "string") continue;
      const cssVar = `--color-${kebab(key)}`;
      previous.push([cssVar, root.style.getPropertyValue(cssVar)]);
      root.style.setProperty(cssVar, value);
    }

    return () => {
      for (const [cssVar, prev] of previous) {
        if (prev) {
          root.style.setProperty(cssVar, prev);
        } else {
          root.style.removeProperty(cssVar);
        }
      }
    };
  }, [theme]);

  // Colour scheme (ADR-0078 X9). Stamp `data-theme` on the document root
  // so the dark/system CSS override blocks in globals.css can select. The
  // controlled `colorScheme` prop wins; otherwise follow the persisted
  // setting and re-apply when `setColorScheme()` or another tab changes it.
  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      root.setAttribute("data-theme", colorScheme ?? readStoredColorScheme());
    };
    apply();

    if (colorScheme) return; // controlled — no listeners needed
    const onChange = () => apply();
    window.addEventListener(COLOR_SCHEME_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(COLOR_SCHEME_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [colorScheme]);

  return <>{children}</>;
}

function kebab(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();
}
